
import { createSelector } from '@reduxjs/toolkit';
// Types
import Reducers from 'app/types/Reducers';
import Features from 'app/types/Features';
// Models
import { RootState } from 'app/store';
import { ICurrentAccount } from 'app/models/Account';
// Utils
import { sortByName, sortStrings } from 'app/utilities/SortBy';

export const selectCurrentAccount = (state:RootState) => state[Reducers.Accounts].currentAccount;
export const selectUserAccounts = (state:RootState) => state[Reducers.Accounts].currentUserAccounts;
export const selectLoading = (state:RootState) => state[Reducers.Accounts].loading;

export const selectHcpcsCodesEnabled = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.features ) return false;
    return currentAccount.features.includes(Features.HcpcsCodes);
  }
);

export const selectInvoicingEnabled = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.features ) return false;
    return currentAccount.features.includes(Features.Invoicing);
  }
);

export const selectPatientPortalEnabled = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.features ) return false;
    return currentAccount.features.includes(Features.PatientPortal);
  }
);

export const selectIsDocumentReprocessingEnabled = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.features ) return false;
    return currentAccount.features.includes(Features.DocumentReprocessing);
  }
);

export const selectBillingOverdueWarningsHidden = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.features ) return false;
    return currentAccount.features.includes(Features.BillingHideOverdueWarnings);
  }
);

export const selectAIChatEnabled = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.features ) return false;
    return currentAccount.features.includes(Features.AIChat);
  }
);

export const selectAIChatPlatformPromptsHidden = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.features ) return false;
    return currentAccount.features.includes(Features.AIChatHidePlatformPrompts);
  }
);

export const selectEpisodeAuthorSuggestionEnabled = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.features ) return false;
    return currentAccount.features.includes(Features.EpisodesAuthorSuggestion);
  }
);

export const selectAccountTags = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount || !currentAccount.tags ) return [];
    return [...currentAccount.tags].sort(sortStrings);
  }
);

export const selectSortedUserAccounts = createSelector(
  [ selectUserAccounts ],
  ( userAccounts:ICurrentAccount[] ) => {
    return [...userAccounts].sort((a, b) => sortByName(a, b, 'company'))
  }
);

export const selectSplitInvoicePerCaseEnabled = createSelector(
  [ selectCurrentAccount ],
  ( currentAccount:ICurrentAccount | null ) => {
    if ( !currentAccount ) return false;
    return currentAccount.settings?.billingPreferences?.splitInvoicePerCase || false;
  }
);
